import { normalizePostcode, type AddressLocale } from '../address/normalize';

/**
 * 按邮编定运费。
 *
 * ★ 「1010 到 1072 收 8 块,0600 到 0699 收 12 块」—— 运营脑子里的价目表就是这样的,
 *   不是「先建一个区、给区挂邮编区间、再给区设价」三步。
 *
 * ★ 订单上的邮编框早就去掉了(真实订单一张都没填过),
 *   所以邮编主要从**地址末尾**取:「88 Queen Street, Auckland 1010」→ 1010。
 */

/** 各地邮编的位数;没列的(generic)按 4~6 位找 */
const DIGITS: Partial<Record<AddressLocale, number>> = {
  'en-NZ': 4,
  'en-MY': 5,
  'en-SG': 6,
  'en-US': 5,
  'zh-CN': 6,
};

/**
 * 从地址文字里取邮编。取不到就 ''。
 *
 * ★ 只认**地址末尾**那一段数字(后面最多跟一个国家名):
 *   「1010 Great North Road, Auckland」开头的 1010 是门牌号,当成邮编的话
 *   整片地址都会按错的价收 —— 而且没有任何地方会报错。
 */
export function extractPostcode(address: string | null | undefined, locale: AddressLocale = 'generic'): string {
  const s = (address ?? '').trim();
  if (!s) return '';
  const n = DIGITS[locale];
  const digits = n ? `\\d{${n}}` : '\\d{4,6}';
  // 前面要有别的内容(不能整个地址就是一串数字);后面最多跟一段纯字母:
  // 「, New Zealand」或者马来西亚那种写在城市前面的「50450 Kuala Lumpur」
  const m = new RegExp(`\\S[\\s,]+(${digits})(?:\\s*,?\\s*([A-Za-z][A-Za-z\\s]*))?\\s*$`).exec(s);
  if (!m) return '';
  // ★ 尾巴里带路名词的,前面那串数字是门牌号:「Unit 5, 1010 Great North Road」
  if (m[2] && m[2].toLowerCase().split(/\s+/).some((w) => STREET_WORDS.has(w))) return '';
  return normalizePostcode(m[1]!, locale);
}

const STREET_WORDS = new Set(
  (
    'road rd street st avenue ave drive dr lane ln place pl crescent cres terrace tce way highway hwy ' +
    'boulevard blvd court ct close parade pde grove square sq jalan jln lorong lrg persiaran psn lebuh'
  ).split(' '),
);

/** 订单用的邮编:手填的优先,没填就从地址里取 */
export function receiverPostcodeOf(
  explicit: string | null | undefined,
  address: string | null | undefined,
  locale: AddressLocale = 'generic',
): string {
  const e = normalizePostcode(explicit ?? '', locale);
  return e || extractPostcode(address, locale);
}

export interface PostcodePriceRange {
  id: string;
  /** 已规整化(normalizePostcode)的起止,含两端 */
  postcodeFrom: string;
  postcodeTo: string;
  /** FIXED = 具体运费(就收这个数);REMOTE = 按偏远处理,运费照算再加这个数 */
  mode: 'FIXED' | 'REMOTE';
  /** 金额(最小货币单位):FIXED 是运费,REMOTE 是偏远加价 */
  price: number;
  /** 只对 FIXED:超重、多件、里程、代收手续费要不要照规则另加 —— 默认不加 */
  includeExtras: boolean;
}

/** 两个邮编比大小:都是纯数字就按数值比,否则按字符串比 */
function cmp(a: string, b: string): number {
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) return Number(a) - Number(b);
  return a < b ? -1 : a > b ? 1 : 0;
}

function width(r: PostcodePriceRange): number {
  return /^\d+$/.test(r.postcodeFrom) && /^\d+$/.test(r.postcodeTo)
    ? Number(r.postcodeTo) - Number(r.postcodeFrom)
    : Number.MAX_SAFE_INTEGER;
}

/**
 * 这个邮编落在哪个区间里。没有就 null。
 *
 * ★ 区间重叠时取**最窄**的:在「1000~1099 收 8 块」里单独配一条「1050~1050 收 15 块」,
 *   就是「这一个邮编要单独算」—— 和画范围「圈套圈取小的」是同一个道理。
 */
export function matchPostcodePrice<T extends PostcodePriceRange>(postcode: string, ranges: readonly T[]): T | null {
  if (!postcode) return null;
  let best: T | null = null;
  for (const r of ranges) {
    if (cmp(postcode, r.postcodeFrom) < 0 || cmp(postcode, r.postcodeTo) > 0) continue;
    if (!best || width(r) < width(best)) best = r;
  }
  return best;
}
